<script setup lang="ts">
import { onMounted, onUnmounted, reactive } from "vue";
import Modal from "@/components/Modal.vue";
import { createFocusTrap, FocusTrap } from "focus-trap";

export type State = {
    showCloseModal: boolean,
}

export interface Props {
    name?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (on:"save"): void,
    (on:"close"): void,
}>();

let focusTrap: FocusTrap | null = null;

onMounted(() => {
    if (!props.name) {
        return;
    }

    const element = document.getElementById(`item-${props.name}`);
    if (element) {
        createTrap(element);
    }
});

onUnmounted(() => {
    if (focusTrap) {
        focusTrap.deactivate();
        focusTrap = null;
    }
});

function createTrap(element: HTMLElement) {
    if (element) {
        focusTrap = createFocusTrap(element, {
            escapeDeactivates: false,
            allowOutsideClick: true,
            returnFocusOnDeactivate: true,
            initialFocus: "#close-item",
        });

        focusTrap.activate();
    }
}

const state: State = reactive({
    showCloseModal: false,
});

const handleSave = () => {
    if (focusTrap) {
        focusTrap.deactivate();
        focusTrap = null;
    }
    emit("save");
    state.showCloseModal = false;
};

const handleClose = () => {
    if (focusTrap) {
        focusTrap.deactivate();
        focusTrap = null;
    }
    emit("close");
    state.showCloseModal = false;
};

</script>
<template>
    <div class="mask" />
    <div
        :id="`item-${name}`"
        class="border border-info bg-white position-relative rounded mt-3 p-4 medical-frame d-flex flex-column z-1"
    >
        <p class="alert alert-info font-weight-bold-col">
            {{ name }}
            <button
                id="close-item"
                class="close-1 position-absolute end-0 top-50 translate-middle"
                @click="state.showCloseModal = true"
            >
                <span>&times;</span>
            </button>
        </p>
        <slot />
        <button
            id="save-item"
            type="button"
            class="btn btn-primary btn-block font-weight-bold align-self-center"
            @click="handleSave"
        >
            Save
        </button>
    </div>
    <Modal
        id="close-item"
        header="Before you close"
        open-focus="close-close-item"
        :show="state.showCloseModal"
        @close="state.showCloseModal = false"
    >
        <template #body>
            <p>Do you want to save your changes or close without saving</p>
        </template>
        <template #footer>
            <button
                id="confirm-close"
                type="button"
                class="btn btn-danger mx-auto mx-sm-0"
                @click.prevent="handleClose"
            >
                Close and lose unsaved changes
            </button>
            <button
                id="confirm-save"
                type="button"
                class="btn btn-primary font-weight-bold mx-auto mx-sm-0"
                @click.prevent="handleSave"
            >
                Save changes
            </button>
        </template>
    </Modal>
</template>

<style>
.mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    background: linear-gradient(to bottom, hsl(189, 30%, 80%), hsl(0, 0%, 80%));
    opacity: 0.6;
}
</style>
